import { ChevronLeft, ChevronRight, PanelLeft, PanelRight, Search, Star } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { pageTitle } from "../navigation";
import { useAppHistory } from "./useAppHistory";
import { shortcuts } from "./useAppShortcuts";
import { isDetailPage, usePageVisits } from "./usePageVisits";
import { useStarredPages } from "./useStarredPages";
import { useStarredChats } from "./useStarredChats";
import styles from "./AppTopBar.module.css";

export interface AppTopBarProps {
  pathname: string;
  isSidebarOpen: boolean;
  isPhone?: boolean;
  inert?: boolean;
  onToggleSidebar: () => void;
  onOpenSearch: () => void;
  isAssistantOpen: boolean;
  onAssistantToggle: () => void;
  /** True while a chat waits for the closed right panel. The toggle shows a dot. */
  hasAssistantHandoff?: boolean;
  /** Receive the elements where a page's TopBarActions render. */
  onLeadingSlot?: (element: HTMLElement | null) => void;
  onActionsSlot?: (element: HTMLElement | null) => void;
}

/**
 * The shared bar over the page: the page name, star and right panel toggle.
 * With the sidebar hidden
 * it also holds the sidebar toggle, back and forward, and search, which
 * otherwise sit at the top of the sidebar.
 */
export function AppTopBar({
  pathname,
  isSidebarOpen,
  isPhone = false,
  inert = false,
  onToggleSidebar,
  onOpenSearch,
  isAssistantOpen,
  onAssistantToggle,
  hasAssistantHandoff = false,
  onLeadingSlot,
  onActionsSlot,
}: AppTopBarProps) {
  const { back, forward, canGoBack, canGoForward } = useAppHistory();
  const { page, section } = pageTitle(pathname);
  const visits = usePageVisits();
  const { isStarred, toggleStar } = useStarredPages();
  const chats = useStarredChats();
  const chatId = /^\/chat\/([^/]+)\/?$/.exec(pathname)?.[1];
  const detailPage = isDetailPage(pathname);
  const episodePage = /^\/episodes\/[^/]+\/?$/.test(pathname) ||
    /^\/datasets\/[^/]+\/episodes\/[^/]+\/?$/.test(pathname);
  const visit = visits.find((v) => v.path === pathname);
  const starred = chatId ? chats.isStarred(chatId) : isStarred(pathname);
  const showNavigation = isPhone || !isSidebarOpen;
  const navigationLabel = isPhone
    ? (isSidebarOpen ? "Hide navigation" : "Show navigation")
    : "Show sidebar";
  const actionsRef = useRef<HTMLElement | null>(null);
  const setActionsSlot = useCallback((element: HTMLElement | null) => {
    actionsRef.current = element;
    onActionsSlot?.(element);
  }, [onActionsSlot]);

  useEffect(() => {
    const actions = actionsRef.current;
    if (!actions) return;
    let frame = 0;
    // Page actions enter through portals, so listen on the DOM slot.
    const revealFocusedAction = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (document.activeElement !== target) return;
        const bounds = actions.getBoundingClientRect();
        const focused = target.getBoundingClientRect();
        const padding = parseFloat(getComputedStyle(actions).scrollPaddingInlineStart) || 0;
        const left = bounds.left + actions.clientLeft + padding;
        const right = bounds.left + actions.clientLeft + actions.clientWidth - padding;
        if (focused.left < left) actions.scrollLeft += focused.left - left;
        else if (focused.right > right) actions.scrollLeft += focused.right - right;
      });
    };
    actions.addEventListener("focusin", revealFocusedAction);
    return () => {
      cancelAnimationFrame(frame);
      actions.removeEventListener("focusin", revealFocusedAction);
    };
  }, []);

  return (
    <header
      className={styles.bar}
      role="banner"
      data-sidebar={isSidebarOpen ? "open" : "closed"}
      data-phone={isPhone ? "true" : undefined}
      inert={inert}
    >
      <div className={styles.cluster}>
        {showNavigation ? (
          <button
            type="button"
            className={styles.iconButton}
            onClick={onToggleSidebar}
            aria-pressed={isPhone ? isSidebarOpen : undefined}
            aria-label={navigationLabel}
            title={navigationLabel}
          >
            <PanelLeft className={styles.icon} aria-hidden="true" />
          </button>
        ) : null}
        {showNavigation ? (
          <>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.historyButton}`}
              onClick={back}
              disabled={!canGoBack}
              aria-label="Go back"
              title={`Back (${shortcuts.back})`}
            >
              <ChevronLeft className={styles.icon} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.historyButton}`}
              onClick={forward}
              disabled={!canGoForward}
              aria-label="Go forward"
              title={`Forward (${shortcuts.forward})`}
            >
              <ChevronRight className={styles.icon} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.searchButton}`}
              onClick={onOpenSearch}
              aria-label="Search"
              title={`Search (${shortcuts.commandPalette})`}
            >
              <Search className={styles.icon} aria-hidden="true" />
            </button>
          </>
        ) : null}
      </div>
      <div ref={onLeadingSlot} className={styles.leading} />
      <h1 className={styles.title}>
        <span className={styles.name} data-detail={detailPage && !episodePage && visit ? "true" : undefined}>
          {chatId ? "Chat" : episodePage ? "Episode" : detailPage && visit ? visit.label : page}
        </span>
        {episodePage ? null : detailPage && visit ? (
          <span className={styles.section}>{visit.context ?? page}</span>
        ) : section ? (
          <span className={styles.section}>{section}</span>
        ) : null}
      </h1>
      <button
        type="button"
        className={styles.iconButton}
        onClick={() =>
          chatId ? chats.toggleStar(chatId) :
            toggleStar({ path: pathname, label: visit?.label ?? page, context: visit?.context ?? section })
        }
        aria-pressed={starred}
        aria-label={starred ? "Unstar" : "Star"}
        title={starred ? "Unstar" : "Star, to keep it in the sidebar"}
      >
        <Star className={styles.starIcon} fill={starred ? "currentColor" : "none"} aria-hidden="true" />
      </button>
      <div ref={setActionsSlot} className={styles.actions} />
      <button
        type="button"
        className={`${styles.iconButton} ${styles.end}`}
        onClick={onAssistantToggle}
        aria-pressed={isAssistantOpen}
        aria-label={isAssistantOpen ? "Hide the right panel" : hasAssistantHandoff ? "Show the right panel. The chat continues there." : "Show the right panel"}
        title={isAssistantOpen ? "Hide the right panel" : "Show the right panel"}
        data-testid="app-right-panel-toggle"
      >
        <PanelRight className={styles.icon} aria-hidden="true" />
        {hasAssistantHandoff ? <span className={styles.dot} aria-hidden="true" /> : null}
      </button>
    </header>
  );
}
